/**
 * Clean schema for deployment by removing all underscore fields
 * This ensures Google validation passes
 */
export function cleanSchemaForDeployment(schema: any): any {
  if (typeof schema !== 'object' || schema === null) {
    return schema;
  }

  // If it's an array, clean each element
  if (Array.isArray(schema)) {
    return schema.map(item => cleanSchemaForDeployment(item));
  }

  // Create a new object without underscore properties
  const cleaned: any = {};
  
  // CRITICAL: @context must be first if it exists
  if (schema['@context']) {
    cleaned['@context'] = schema['@context'];
  }
  
  // Then @type if it exists
  if (schema['@type']) {
    cleaned['@type'] = schema['@type'];
  }
  
  // Then @id if it exists
  if (schema['@id']) {
    cleaned['@id'] = schema['@id'];
  }
  
  // Process all other properties
  for (const [key, value] of Object.entries(schema)) {
    // Skip if already added above
    if (key === '@context' || key === '@type' || key === '@id') {
      continue;
    }
    
    // Skip all underscore properties
    if (key.startsWith('_')) {
      continue;
    }
    
    // Recursively clean nested objects
    cleaned[key] = cleanSchemaForDeployment(value);
  }

  return cleaned;
}

/**
 * Extract metadata from schema (all underscore fields)
 */
export function extractSchemaMetadata(schema: any): Record<string, any> {
  const metadata: Record<string, any> = {};
  
  function extract(obj: any, path: string = '') {
    if (typeof obj !== 'object' || obj === null) {
      return;
    }
    
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = path ? `${path}.${key}` : key;
      
      if (key.startsWith('_')) {
        metadata[fullPath] = value;
      } else if (typeof value === 'object') {
        extract(value, fullPath);
      }
    }
  }
  
  extract(schema);
  return metadata;
}

/**
 * Validate schema is ready for deployment
 */
export function validateSchemaForDeployment(schema: any): { 
  valid: boolean; 
  errors: string[] 
} {
  const errors: string[] = [];
  
  // Check @context is first
  const keys = Object.keys(schema);
  if (keys[0] !== '@context') {
    errors.push('@context must be the first property');
  }
  
  // Check for underscore fields
  function checkForUnderscores(obj: any, path: string = ''): void {
    if (typeof obj !== 'object' || obj === null) {
      return;
    }
    
    for (const [key, value] of Object.entries(obj)) {
      if (key.startsWith('_')) {
        errors.push(`Underscore field found: ${path ? path + '.' : ''}${key}`);
      } else if (typeof value === 'object') {
        checkForUnderscores(value, path ? `${path}.${key}` : key);
      }
    }
  }
  
  checkForUnderscores(schema);
  
  // Basic structure validation
  if (!schema['@context']) {
    errors.push('Missing @context');
  }
  
  if (!schema['@type']) {
    errors.push('Missing @type');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}